import { ChangeEvent, KeyboardEvent, useCallback, useEffect, useId, useRef, useState } from "react";
import styled from "@emotion/styled";
import { ApiService, InputStyle, ValidationErrorStyle } from "src/common";
import { errorA11yProps, errorIdFor } from "src/common/style/a11y.style";
import { InputLabel } from "src/components";
import { colors } from "src/theme";

const Box = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px'
})

const CityList = styled.ul({
  margin: 0,
  listStyle: 'none',
  width: '100%',
  boxSizing: 'border-box' as const,
  outline: 'none',
  display: 'block',
  padding: '9px 12px',
  fontSize: '18px',
  fontWeight: 400,
  fontStyle: 'normal',
  lineHeight: '24px',
  color: colors.text1,
  borderRadius: '8px',
  border: `1px solid ${colors.border5}`,
  transition: 'border-color .15s ease-in-out, box-shadow .15s ease-in-out',
  backgroundColor: '#fff',
  maxHeight: '150px',
  overflowY: 'auto' as const,
  zIndex: 99,
});

const CityItem = styled.li((props: { isActive?: boolean }) => ({
  cursor: 'pointer',
  padding: '2px 4px',
  borderRadius: '4px',
  backgroundColor: props.isActive ? colors.background6 : 'transparent',
  outline: props.isActive ? `2px solid ${colors.primary}` : 'none',
  outlineOffset: '-2px',

  '&:hover': {
    backgroundColor: colors.background6,
  }
}))

export type InputFieldPropsCity = {
  id?: string;
  label?: string;
  value?: string | number;
  error?: string;
  disabled?: boolean;
  onChange?: (value: { city: string, cityKompareId: string, zipCode: number, street: string, streetKompareId: string }, isValid: boolean) => void;
};

interface CityData {
  readonly cityKompareId: string;
  readonly cityName: string;
  readonly cityParentName: string;
  readonly postalCode: number;
}

export const InputCity: React.FC<InputFieldPropsCity> = ({
  label,
  value,
  error,
  disabled,
  onChange
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [showCityList, setShowCityList] = useState(false);
  const [citySelected, setCitySelected] = useState(false);
  const [inputTimeout, setInputTimeout] = useState<NodeJS.Timeout | null>(null);
  const [cityData, setCityData] = useState<CityData[]>([]);
  // Index in cityData highlighted by the keyboard, -1 when none
  const [activeIndex, setActiveIndex] = useState(-1);
  const listRef = useRef<HTMLUListElement>(null);
  // Chrome ignores autocomplete="off" on fields it classifies as address fields (from the id, name or
  // label text, e.g. "Ulica"), so the input gets a neutral generated id and an unrecognized
  // autocomplete token, which Chrome does not autofill
  const inputId = `combobox${useId().replace(/:/g, '')}`;
  const listboxId = `${inputId}-listbox`;
  const optionId = (index: number) => `${inputId}-option-${index}`;

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const fetchCities = (query: string) => {
    ApiService.getCities(query).subscribe({
      next: (cities: CityData[]) => {
        setCityData(cities);
        setActiveIndex(-1);
      }
    });
  };

  const fetchCitiesDebounced = useCallback((query: string) => {
    if (inputTimeout) {
      clearTimeout(inputTimeout);
    }

    const timeout = setTimeout(() => {
      fetchCities(query);
    }, 500);

    setInputTimeout(timeout);
  }, [inputTimeout]);

  const resetCitySelected = () => {
    setCitySelected(false);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const newValue = e.target.value;

    setInputValue(newValue);

    fetchCitiesDebounced(newValue);

    setShowCityList(true);
    resetCitySelected();
  };

  const handleCityItemClick = (city: CityData) => {
    setInputValue(`${city.cityName}, ${city.postalCode}`);
    setCitySelected(true);
    setShowCityList(false);
    setActiveIndex(-1);
    if (onChange) {
      onChange({ cityKompareId: city.cityKompareId, city: city.cityName, zipCode: city.postalCode, street: '', streetKompareId: '' }, !!city.cityKompareId);
    }
  };

  const selectableCities = cityData.filter(city => city.cityName);
  const isExpanded = showCityList && selectableCities.length > 0 && !citySelected;

  // Keep the highlighted option scrolled into view
  useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    listRef.current.querySelector<HTMLElement>(`#${CSS.escape(optionId(activeIndex))}`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const count = selectableCities.length;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (count === 0) return;
        if (!isExpanded) { setShowCityList(true); setCitySelected(false); }
        setActiveIndex(prev => (prev + 1) % count);
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (count === 0) return;
        if (!isExpanded) { setShowCityList(true); setCitySelected(false); }
        setActiveIndex(prev => (prev <= 0 ? count - 1 : prev - 1));
        break;
      case 'Enter':
        if (isExpanded && activeIndex >= 0 && activeIndex < count) {
          e.preventDefault();
          handleCityItemClick(selectableCities[activeIndex]);
        }
        break;
      case 'Escape':
        if (isExpanded) {
          e.preventDefault();
          setShowCityList(false);
          setActiveIndex(-1);
        }
        break;
      case 'Tab':
        setShowCityList(false);
        setActiveIndex(-1);
        break;
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => () => {
    if (inputTimeout) {
      clearTimeout(inputTimeout);
    }
  }, [inputTimeout]);

  return (
    <Box>
      <div>
        {label ? <InputLabel htmlFor={inputId} disabled={disabled}>{label}</InputLabel> : null}
        <InputStyle
          autoComplete="kompare-off"
          id={inputId}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={isExpanded}
          aria-controls={listboxId}
          aria-activedescendant={isExpanded && activeIndex >= 0 ? optionId(activeIndex) : undefined}
          {...errorA11yProps(inputId, error)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            setShowCityList(false);
            setActiveIndex(-1);
          }}
          type='text'
          value={inputValue}
          disabled={disabled}
          onChange={handleChange}
          hasError={!!error}
        />
        {error ? <ValidationErrorStyle id={errorIdFor(inputId)}>{error}</ValidationErrorStyle> : null}
      </div>
      {showCityList && cityData && cityData.length > 0 && !citySelected && (
        <CityList id={listboxId} role="listbox" ref={listRef} aria-label={label}>
          {selectableCities.length === 0 ? (
            <CityItem role="option" aria-disabled="true">Nema rezultata</CityItem>
          ) : (
            selectableCities.map((city, index) => (
              <CityItem
                key={city.cityKompareId}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                isActive={index === activeIndex}
                // Keep focus in the input so blur does not close the list before the click lands
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleCityItemClick(city)}
              >
                {`${city.cityName}, ${city.postalCode}`}
              </CityItem>
            ))
          )}
        </CityList>
      )}
    </Box>
  );
};
